import { Button, Grid, useTheme } from '@material-ui/core';
import { equals } from 'ramda';
import { Fragment, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { PageLoader } from '~components/Page';
import { userHasPermissions } from '~features/users/usersUtils';
import { Alarm } from '~models/alarm';
import { intToHex } from '~utils/hex';
import { getLinkByUnitType } from '~utils/index';

import { UnitInfo, getTitle, getUnitTypePath } from './UnitsDialog.config';

import { PermissionName } from '~models';
import { boundActions, selectors } from '~store';
import { Dialog, DialogContent, DialogActions, DialogTitle, LinkButton } from '~ui-kit';

type UnitContactsInfoDialogProps = {
  open: boolean;
  unit: UnitInfo;
  alarm: Alarm;
  onClose: () => void;
  backTo?: string;
};

export const UnitContactsInfoDialog = ({ open = false, unit, alarm, onClose, backTo }: UnitContactsInfoDialogProps) => {
  if (!open) {
    return null;
  }
  const link = getLinkByUnitType(unit.unitType, unit.businessUnitId, unit.unitId, backTo);
  const { unitDetails: loading } = useSelector(selectors.alarms.getAlarmsLoaders, equals);
  const unitDetails = useSelector(selectors.alarms.getUnitDetails, equals);
  const customerName = unitDetails?.customer as string;
  const customerIds = useSelector(selectors.customers.getCustomersList, equals);
  const contacts = useSelector(selectors.alarms.getUnitContacts, equals);
  const { details: listLoading } = useSelector(selectors.customers.getCustomerLoaders, equals);
  const { contacts: contactsLoading } = useSelector(selectors.alarms.getAlarmsLoaders, equals);
  const isLoading = loading || listLoading || contactsLoading;
  const currentUser = useSelector(selectors.auth.getCurrentUser, equals);
  const hasPermissions = userHasPermissions(currentUser, [PermissionName.CUSTOMER_VIEW]);

  const customerId = customerIds?.filter(cus => cus.name === customerName)?.[0]?.id as number;
  const customerInfo = useSelector(selectors.customers.getCustomerById(customerId), equals);
  const { spacing } = useTheme();

  useEffect(() => {
    boundActions.alarms.fetchUnitDetailsInit({
      businessUnitId: unit.businessUnitId,
      id: unit.unitId,
      unitType: getUnitTypePath(unit.unitType),
    });
    boundActions.alarms.fetchUnitContactsInit({
      buId: unit.businessUnitId,
      id: unit.unitId,
      unitType: getUnitTypePath(unit.unitType),
    });
    if (hasPermissions) {
      boundActions.customers.fetchCustomersIdsInit();
    }
  }, [unit]);

  useEffect(() => {
    if (customerId && hasPermissions) {
      boundActions.customers.fetchCustomerByIdInit({ id: customerId });
    }
  }, [customerId]);

  return (
    <Dialog fullWidth maxWidth="sm" open={open}>
      <DialogTitle title="Contact Info" />
      <DialogContent>
        {isLoading ? (
          <PageLoader />
        ) : (
          <Grid container>
            <Grid item xs={12}>
              {getTitle(alarm.unitId, alarm.unitType)}
            </Grid>
            <Grid item xs={12}>
                Address: {alarm?.address1 || '-'}
            </Grid>
            {contacts.map(contact =>
              <Fragment key={contact.id}>
                <Grid item xs={12} style={{ marginTop: spacing(2) }}>
                  Contact Name: {contact.contactName || '-'}
                </Grid>
                <Grid item xs={12}>
                  Contact Phone: {contact.phoneNumber || '-'}
                </Grid>
                <Grid item xs={12}>
                  Contact Position: {contact.position || '-'}
                </Grid>
                <Grid item xs={12}>
                  Contact Details: {contact.details || '-'}
                </Grid>
              </Fragment>
            )}

            {customerInfo &&
              <Fragment>
                <Grid item xs={12} style={{ marginTop: spacing(2) }}>
                  Customer Name: {customerInfo?.name || '-'}
                </Grid>
                <Grid item xs={12}>
                  Customer Phone Number: {customerInfo?.phoneNumber || '-'}
                </Grid><Grid item xs={12}>
                  Customer Email: {customerInfo?.emailAddress || '-'}
                </Grid><Grid item xs={12}>
                  Customer Notes: {customerInfo?.notes || '-'}
                </Grid>
              </Fragment>
            }

          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose}>
          Close
        </Button>
        <LinkButton variant="contained" color="primary" linkTo={link}>
          Open {intToHex(unit.unitId)}
        </LinkButton>
      </DialogActions>
    </Dialog>
  );
};
